'use client';

import { DatePicker as DSDatePicker } from '@lulalife/design-system';
import { useCallback } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import { parseLocal } from 'src/helper/date';

/** `Date` -> the form's stored string, in the caller's own format, read in LOCAL time (never `new Date(str)` — that reads UTC midnight, which is the previous day west of Greenwich). */
const toDateString = (
  value: Date | [Date, Date] | null,
  dateFormat: string,
): string => {
  const single = Array.isArray(value) ? value[0] : value;
  if (!single) return '';
  return parseLocal(single)?.format(dateFormat) ?? '';
};

/** The form's stored string -> `Date`, same local-time reading, `null` for `''`. */
const toDate = (value: string): Date | null => parseLocal(value)?.toDate() ?? null;

interface DatePickerFieldProps {
  name: string;
  label?: string;
  placeholderText: string;
  /** The format the form stores the date as — same as the legacy `DatePicker`'s required `dateFormat`, e.g. `'YYYY-MM-DD'` or `'MMM DD, YYYY'`. Every caller sets its own; there is no app-wide default. */
  dateFormat: string;
  hideFormError?: boolean;
  className?: string;
  minDate?: Date | number;
  maxDate?: Date | number;
  mandatory?: boolean;
  disabled?: boolean;
  /** DS supports exactly the same two values as the legacy picker's real usage (1 or 2 visible months). */
  numberOfMonths?: 1 | 2;
  handleChange?: null | ((date: Date | Date[] | null) => unknown);
  /** Names the `role="combobox"` trigger when no visible `label` is drawn — the settings-row case, where the visible label already lives in the row's left column. Same channel as `Select`'s `aria-label`. */
  'aria-label'?: string;
}

/**
 * Same `name`-bound, `useController`-wired API as
 * `src/components/Shared/FormFields/DatePicker.tsx` — only the rendered
 * control changed, from `react-multi-date-picker` to the design system's
 * `DatePicker`. A call site swaps the import and keeps every prop it already
 * passes.
 *
 * VALUE-SHAPE CONVERSION LIVES HERE, NOT IN THE FORM MODEL, same as the legacy
 * component: the form keeps storing whatever string shape `dateFormat` names —
 * callers in this app use both `'YYYY-MM-DD'` (User Details' GL insurance
 * expiry) and `'MMM DD, YYYY'` (the listing filters, the style-guide) — so
 * `buildUpdatePayload` and every existing consumer are untouched. DS
 * `DatePicker` only speaks `Date | [Date, Date] | null`. `handleChange`, when
 * given, still runs on the converted `Date` before the result reaches the
 * form — same contract the legacy picker had.
 *
 * DS-GAP(DatePicker naming its trigger externally) — CLOSED in design-system
 * 0.18.0 (ORI-762): DS `DatePicker` now accepts `id` / `aria-label` /
 * `aria-labelledby` and threads them to its `role="combobox"` trigger, exactly
 * the way `Select` did at 0.14.0. This wrapper surfaces `aria-label` (below) so
 * a settings row can name the control without drawing a second visible label —
 * see `Select.tsx` for the identical `aria-label={label ? undefined : …}` shape.
 *
 * Deliberately not carried over: `enableTimePicker`, `multiple`, `range`,
 * `enableCustomRange`, `enableFooterDateRange`, `portal`, `renderCustomElement`,
 * `mapDays`, `iconPosition`, `withTimePicker`, `handleClear`,
 * `customHandleChange` — none of this app's current date-field usage (a
 * single date, no range, no time, no manual clear/typed-entry handler) passes
 * any of them, and DS `DatePicker` has no equivalent slots for the
 * range/multi/time modes.
 *
 * ONE REAL GAP REMAINS (`highlightToday`); `ref` is now resolved upstream but
 * deliberately not surfaced here:
 *   - `highlightToday`: this field's own legacy `react-multi-date-picker`
 *     rendered `highlightToday={false}` (dropped in the #1367 DS conversion of
 *     User Details); DS `DatePicker` has no equivalent — the calendar's "is
 *     this today" styling is not configurable through any prop. A separate
 *     follow-up. (The one remaining `highlightToday={false}` in the tree is the
 *     Calendar page's inline calendar, a different control.)
 *   - `ref` (used on the listing filters' and BlockOutTimeModal's date fields,
 *     for imperative focus/open-calendar control): DS `DatePicker` became
 *     `forwardRef<HTMLInputElement>` in 0.18.0, so the upstream capability now
 *     exists — but this wrapper still leaves `field.ref` unused, the same as
 *     every other DS control in this app. A caller that needs an imperative ref
 *     would surface it here at that point; no current consumer does.
 */
export const DatePicker = (props: DatePickerFieldProps) => {
  const {
    name,
    label,
    placeholderText,
    dateFormat,
    hideFormError = false,
    className,
    minDate,
    maxDate,
    mandatory,
    disabled = false,
    numberOfMonths,
    handleChange = null,
    'aria-label': ariaLabelProp,
  } = props;

  const { control } = useFormContext();
  const {
    field: { onChange, value },
    formState: { errors },
  } = useController({ name, control });

  const fieldError = get(errors, name);
  const fieldErrorMessage = typeof fieldError?.message === 'string' ? fieldError.message : undefined;

  const handleDateChange = useCallback(
    (next: Date | [Date, Date] | null) => {
      // `mode="single"` above means DS only ever hands back a single `Date` or
      // `null` here, never the range-tuple shape — the array branch of the type
      // is unreachable in practice, matching the legacy picker's own contract
      // with `handleChange` (a single `Date | Date[] | null`).
      const single = Array.isArray(next) ? next[0] : next;
      if (handleChange) {
        onChange(handleChange(single));
      } else {
        onChange(toDateString(single, dateFormat));
      }
    },
    [onChange, handleChange, dateFormat],
  );

  return (
    // SHIM(ORI-762): DS `DatePicker` hardcodes `onFocus={openCalendar}` on its
    // `role="combobox"` trigger and exposes no prop to disable it — so Tab-focusing
    // the field auto-opens the calendar, flips `aria-expanded` to true, and a screen
    // reader announces "expanded" instead of the field's name (QA failed exactly this
    // on VoiceOver). We stop the focus event from reaching that trigger's internal
    // `onFocus` in the capture phase, so the calendar opens on click / Enter / Space
    // only (the WAI-ARIA combobox pattern) and the name announces cleanly. The guard
    // keeps this to the combobox trigger, leaving the calendar's own cells' focus
    // untouched. `contents` adds no box, so layout is unchanged.
    // REMOVE when the design system stops auto-opening on focus (or exposes an
    // `openOnFocus` prop) — see the ORI-762 investigation.
    <div
      className="contents"
      onFocusCapture={(e) => {
        if ((e.target as HTMLElement).getAttribute('role') === 'combobox') {
          e.stopPropagation();
        }
      }}
    >
      <DSDatePicker
        mode="single"
        label={mandatory && label ? `${label} *` : label}
        aria-label={label ? undefined : (ariaLabelProp ?? name)}
        placeholder={placeholderText}
        className={className}
        value={toDate(value)}
        onChange={handleDateChange}
        minDate={minDate instanceof Date ? minDate : undefined}
        maxDate={maxDate instanceof Date ? maxDate : undefined}
        numberOfMonths={numberOfMonths}
        disabled={disabled}
        required={mandatory}
        error={hideFormError ? false : Boolean(fieldError)}
        errorMessage={hideFormError ? undefined : fieldErrorMessage}
      />
    </div>
  );
};

export default DatePicker;
